/**
 * FriendlyTip — ONE shared dismissible tip card.
 *
 * Card surface + subtle accent border so it sits with the other Command Center
 * cards in BOTH themes (the shared InfoBanner is a pale-lavender panel that
 * glares in dark mode, and it is used elsewhere, so it is left untouched).
 * Vertical-agnostic: no tip keys => renders nothing (never generic copy).
 * Dismissal is remembered per browser (localStorage only, no backend).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, X } from 'lucide-react';

const key = (id) => `yulontap.friendlyTip.dismissed.${id}`;

const readDismissed = (id) => {
    try { return window.localStorage.getItem(key(id)) === '1'; } catch { return false; }
};

export function FriendlyTip({ id, titleKey, bodyKey, className = '' }) {
    const { t } = useTranslation();
    const [hidden, setHidden] = useState(() => readDismissed(id));

    if (!id || !bodyKey || hidden) return null;

    return (
        <div
            data-testid={`friendly-tip-${id}`}
            className={`flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-white px-4 py-3 shadow-sm dark:bg-slate-900/40 dark:shadow-none ${className}`}
        >
            <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
            <div className="min-w-0 flex-1">
                {titleKey && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                        {t(titleKey)}
                    </p>
                )}
                <p className="mt-0.5 text-sm text-slate-700 break-words dark:text-slate-200"
                    data-testid={`friendly-tip-body-${id}`}>
                    {t(bodyKey)}
                </p>
            </div>
            <button
                type="button"
                aria-label={t('commandCenter.friendly.tour.close')}
                data-testid={`friendly-tip-dismiss-${id}`}
                onClick={() => {
                    try { window.localStorage.setItem(key(id), '1'); } catch { /* storage off */ }
                    setHidden(true);
                }}
                className="shrink-0 text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-white"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

export default FriendlyTip;
